import { useState, useEffect, useCallback } from 'react'

export function useServiceWorker() {
  const [waitingWorker, setWaitingWorker] = useState(null)
  const [showUpdateBanner, setShowUpdateBanner] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [registration, setRegistration] = useState(null)

  const registerSW = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.log('[useServiceWorker] Service Workers not supported')
      return null
    }

    try {
      const reg = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none' // Always check for updates
      })
      
      console.log('[useServiceWorker] SW registered:', reg)
      setRegistration(reg)

      // Check if there's already a waiting worker
      if (reg.waiting) {
        console.log('[useServiceWorker] Found waiting worker on register')
        setWaitingWorker(reg.waiting)
        setShowUpdateBanner(true)
      }

      // Listen for new workers
      reg.addEventListener('updatefound', () => {
        console.log('[useServiceWorker] Update found!')
        const newWorker = reg.installing

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            console.log('[useServiceWorker] New worker state:', newWorker.state)
            
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('[useServiceWorker] New version installed, showing banner')
              setWaitingWorker(newWorker)
              setShowUpdateBanner(true)
            }
          })
        }
      })

      // Check for updates periodically (every 60 seconds)
      setInterval(() => {
        console.log('[useServiceWorker] Checking for updates...')
        reg.update().catch(err => console.error('[useServiceWorker] Update check failed:', err))
      }, 60000)

      // Also check on visibility change (when user returns to tab)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('[useServiceWorker] Tab visible, checking for updates...')
          reg.update().catch(err => console.error('[useServiceWorker] Update check failed:', err))
        }
      })

      return reg
    } catch (error) {
      console.error('[useServiceWorker] Registration failed:', error)
      return null
    }
  }, [])

  const updateServiceWorker = useCallback(() => {
    if (!waitingWorker) {
      console.log('[useServiceWorker] No waiting worker to update')
      return
    }

    setIsUpdating(true)
    console.log('[useServiceWorker] Triggering update...')

    // Listen for the new worker to take control
    const onControllerChange = () => {
      console.log('[useServiceWorker] Controller changed, reloading...')
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true })

    // Tell the waiting worker to skip waiting and become active
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })

    // Fallback reload after 3 seconds if controllerchange doesn't fire
    setTimeout(() => {
      console.log('[useServiceWorker] Fallback reload')
      window.location.reload()
    }, 3000)
  }, [waitingWorker])

  const dismissUpdate = useCallback(() => {
    setShowUpdateBanner(false)
  }, [])

  // Listen for messages from SW
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const handleMessage = (event) => {
      console.log('[useServiceWorker] Message from SW:', event.data)
      
      if (event.data?.type === 'SW_ACTIVATED') {
        console.log('[useServiceWorker] SW activated, version:', event.data.version)
        setShowUpdateBanner(false)
        setWaitingWorker(null)
      }
    }

    navigator.serviceWorker.addEventListener('message', handleMessage)
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  }, [])

  // Initial registration
  useEffect(() => {
    registerSW()
  }, [registerSW])

  return {
    showUpdateBanner,
    isUpdating,
    updateServiceWorker,
    dismissUpdate,
    registration
  }
}
